import { useCallback, useEffect, useRef, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Item, ItemTypes, ItemWithMetaData, RatingTypes } from "../api/Items";
import { useFetchItemsForRate } from "../api/ItemsApi";
import PostRateMediaRating, {
  PostRateMediaAction,
} from "./PostRateMediaRating";

import {
  ACTOR_TIMER_STATUS_UI_ENUM,
  ReportSummaryItemApi,
} from "../api/Reports";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import {
  useReportDetail,
  useReportTimer,
} from "../reports/ReportDetailProvider";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import ErrorMessageContainer from "../shared-components/ErrorMessageContainer";
import { usePostManualRateDone } from "../api/WorkflowApi";
import { WORKFLOW_STEP_STATUS_ENUM } from "../api/Workflows";
import { useWorkflowStepActorTimer } from "../reports/useWorkflowStepActorTimer";
import { behaviorRelabelMapping, useFamaFlags } from "../api/FamaFlags";

function progressMatchesPage(
  updatedItems: ItemWithMetaData[],
  posts: ItemWithMetaData[]
): boolean {
  if (updatedItems.length === 0) return true;
  if (updatedItems.length > posts.length) return false;
  return updatedItems.every(
    (item, index) => item.item.external_id === posts[index]?.item.external_id
  );
}

const PAGE_SIZE = 15;

type BoundaryPhase =
  | "idle"
  | "flushing"
  | "loading-next"
  | "load-failed"
  | "completing";

function asPostList(data: unknown): ItemWithMetaData[] {
  if (Array.isArray(data)) {
    return data as ItemWithMetaData[];
  }
  if (
    data != null &&
    typeof data === "object" &&
    Array.isArray((data as { items?: unknown }).items)
  ) {
    return (data as { items: ItemWithMetaData[] }).items;
  }
  return [];
}

const PostRate = ({
  isReadOnly,
  handleExit,
  report,
  isAmendment,
}: {
  isReadOnly: boolean;
  handleExit: () => void;
  report: ReportSummaryItemApi;
  isAmendment: boolean;
}) => {
  const {
    isLoading: postsLoading,
    data: postListData,
    mutate: getPosts,
    isSuccess: isSuccessPostList,
    error: errorPostList,
  } = useFetchItemsForRate();
  const navigate = useNavigate();
  const { canPostRate } = usePermissionValidator();
  const { resetTimer } = useReportTimer();
  const { blueprint, isSuccessLoadingBlueprintAnnotated, isLoadingBlueprint } =
    useReportDetail();
  const {
    isLoading: isLoadingManualRateDone,
    error: errorManualRateDone,
    isSuccess: isSuccessManualRate,
    mutate: postManualRateDone,
  } = usePostManualRateDone();
  const postActorTimer = useWorkflowStepActorTimer(
    report.workflow_run_uuid,
    WORKFLOW_STEP_STATUS_ENUM.PostsManualRate
  );
  const { behaviorMappingActions } = useFamaFlags();
  const [computedBehaviors, setComputedBehaviors] = useState<string[]>([]);
  const [pagePosts, setPagePosts] = useState<ItemWithMetaData[] | null>(null);
  const [updatedItems, setUpdatedItems] = useState<ItemWithMetaData[]>([]);
  const [currentPost, setCurrentPost] = useState<ItemWithMetaData>();
  const [allPostRated, setAllPostRated] = useState(false);
  const [ratedItems, setRatedItems] = useState<string[]>([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [boundaryPhase, setBoundaryPhase] = useState<BoundaryPhase>("idle");
  const pendingOperationsRef = useRef<Set<number>>(new Set());
  const nextOperationIdRef = useRef(0);

  const beginOperation = useCallback(() => {
    const token = ++nextOperationIdRef.current;
    pendingOperationsRef.current.add(token);
    setPendingCount(pendingOperationsRef.current.size);
    return token;
  }, []);

  const endOperation = useCallback((token: number) => {
    pendingOperationsRef.current.delete(token);
    setPendingCount(pendingOperationsRef.current.size);
  }, []);

  const loadPosts = useCallback(() => {
    getPosts(
      {
        report_uuid: report.report_uuid,
        item_type_name: [ItemTypes.SOCIAL_MEDIA_POST],
        rating_type: RatingTypes.Manual,
        page_size: PAGE_SIZE,
      },
      {
        onSuccess: (data) => {
          const posts = asPostList(data);
          // React 17 does not batch setState in mutation callbacks. Without
          // batching, pagePosts/boundaryPhase can commit before updatedItems/
          // ratedItems clear, and the queue effect treats the new page as
          // exhausted (old cursor) and skips it via another loadPosts.
          unstable_batchedUpdates(() => {
            setPagePosts(posts);
            setBoundaryPhase((phase) =>
              phase === "loading-next" || phase === "load-failed"
                ? "idle"
                : phase
            );
            // Clear rated/updated only after a successful fetch replaces the page —
            // clearing earlier lets a failed next-page fetch re-offer already-handled posts.
            setUpdatedItems([]);
            setRatedItems([]);
            if (posts.length > 0) {
              setCurrentPost(posts[0]);
            } else {
              setCurrentPost(undefined);
            }
          });
        },
        onError: () => {
          // Stay failed so we do not immediately re-enter loading-next with the
          // previous page still mounted and rated state intact.
          setBoundaryPhase((phase) =>
            phase === "loading-next" ? "load-failed" : phase
          );
        },
      }
    );
  }, [getPosts, report.report_uuid]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  // Keep pagePosts in sync if mutation data updates without onSuccess (e.g. tests).
  useEffect(() => {
    if (postListData == null) return;
    const posts = asPostList(postListData);
    setPagePosts((prev) => {
      if (
        prev != null &&
        prev.length === posts.length &&
        prev[0]?.item.external_id === posts[0]?.item.external_id
      ) {
        return prev;
      }
      return posts;
    });
  }, [postListData]);

  useEffect(() => {
    if (isSuccessPostList) {
      setComputedBehaviors(
        behaviorRelabelMapping(
          blueprint?.behaviors ?? [],
          behaviorMappingActions
        )
      );
    }
  }, [
    isSuccessPostList,
    behaviorMappingActions,
    postListData,
    blueprint?.behaviors,
  ]);

  const stepDone = useCallback(() => {
    if (isAmendment) {
      navigate("/reports/amendment", { state: { stepDone: true } });
    } else {
      navigate("/reports/next_task", { state: { stepDone: true } });
    }
  }, [isAmendment, navigate]);

  useEffect(() => {
    if (isSuccessManualRate) {
      stepDone();
    }
  }, [navigate, isSuccessManualRate, stepDone]);

  useEffect(() => {
    if (pagePosts == null || allPostRated) return;
    if (boundaryPhase === "completing") return;
    if (boundaryPhase === "load-failed") return;
    // While the next page request is in flight, keep waiting — do not re-fetch.
    if (boundaryPhase === "loading-next") return;

    const posts = pagePosts;
    const postCount = posts.length;
    if (postCount === 0) {
      postActorTimer(ACTOR_TIMER_STATUS_UI_ENUM.CLOSED);
      if (pendingCount > 0) {
        if (boundaryPhase !== "flushing") {
          setBoundaryPhase("flushing");
          setCurrentPost(undefined);
        }
        return;
      }
      setBoundaryPhase("completing");
      postManualRateDone({
        reportUUID: report.report_uuid,
        workflowRunUUID: report.workflow_run_uuid,
      });
      setAllPostRated(true);
      return;
    }

    // Guard against stale progress from a prior page (React 17 unbatched
    // onSuccess, or any partial commit). Old updatedItems.length as cursor
    // would skip the entire new page and fetch again.
    if (!progressMatchesPage(updatedItems, posts)) {
      setUpdatedItems([]);
      setRatedItems([]);
      setCurrentPost(posts[0]);
      if (boundaryPhase === "flushing") {
        setBoundaryPhase("idle");
      }
      return;
    }

    let cursor = updatedItems.length;
    const skippedItems: ItemWithMetaData[] = [];
    while (cursor < postCount) {
      const candidate = posts[cursor];
      if (ratedItems.includes(candidate.item.external_id)) {
        skippedItems.push(candidate);
        cursor += 1;
      } else {
        break;
      }
    }

    if (cursor < postCount) {
      const nextItem = posts[cursor];
      if (boundaryPhase === "flushing") {
        setBoundaryPhase("idle");
      }
      setCurrentPost(nextItem);
      if (skippedItems.length > 0) {
        setUpdatedItems((prev) => [...prev, ...skippedItems]);
      }
      return;
    }

    if (pendingCount > 0) {
      if (boundaryPhase !== "flushing") {
        setBoundaryPhase("flushing");
        setCurrentPost(undefined);
      }
      return;
    }

    // Keep rated/updated until onSuccess replaces the page — a failed fetch must
    // not treat the previous page as unrated again.
    setBoundaryPhase("loading-next");
    setCurrentPost(undefined);
    loadPosts();
  }, [
    pagePosts,
    allPostRated,
    boundaryPhase,
    pendingCount,
    updatedItems,
    ratedItems,
    loadPosts,
    postManualRateDone,
    postActorTimer,
    report,
  ]);

  useEffect(() => {
    if (isSuccessLoadingBlueprintAnnotated && isSuccessPostList) {
      resetTimer?.();
    }
  }, [isSuccessLoadingBlueprintAnnotated, isSuccessPostList, resetTimer]);

  const handleAction = useCallback(
    (verb: PostRateMediaAction, post: Item) => {
      const posts = pagePosts ?? [];

      if (verb === "undo" || verb === "removeFailed") {
        setRatedItems((prev) => prev.filter((ri) => ri !== post.external_id));
        setUpdatedItems((prev) => {
          const restoredIndex = posts.findIndex(
            (item) => item.item.external_id === post.external_id
          );
          if (restoredIndex < 0) return prev;
          // Truncate existing progress only. Rebuilding via posts.slice would
          // re-mark earlier rolled-back in-flight failures as already handled.
          return prev.filter((item) => {
            const idx = posts.findIndex(
              (candidate) =>
                candidate.item.external_id === item.item.external_id
            );
            return idx >= 0 && idx < restoredIndex;
          });
        });
        resetTimer?.();
        return;
      }

      setRatedItems((prev) =>
        prev.includes(post.external_id) ? prev : [...prev, post.external_id]
      );
      setUpdatedItems((prev) => {
        const item = posts.find(
          (candidate) => candidate.item.external_id === post.external_id
        );
        if (!item) return prev;
        if (prev.some((p) => p.item.external_id === post.external_id)) {
          return prev;
        }
        return [...prev, item];
      });
      resetTimer?.();
    },
    [pagePosts, resetTimer]
  );

  const isFlushing = boundaryPhase === "flushing";

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="text-2xl font-extrabold leading-8 tracking-normal text-left">
          Posts To Be Rated
        </div>
        <span
          className="text-xs font-medium text-orange-500 grow-0 cursor-pointer"
          onClick={handleExit}
        >
          Exit Post Rate
        </span>
      </div>
      {isLoadingManualRateDone && <LoadingSpinner />}
      {errorManualRateDone && (
        <ErrorMessageContainer
          message={"Error Rating Posts, please try again or contact support"}
        />
      )}
      {/* WARNING: We are skipping purposly system review, enable once desicion is rolledback or remove if no longer needed */}
      {/* {allPostRated && (
        <AllPostsRated
          handleReviewSystem={handleReview}
          handleExit={handleExit}
          isReadOnly={isReadOnly}
        />
      )} */}
      {!postsLoading && !isFlushing && currentPost && !allPostRated && (
        <PostRateMediaRating
          behaviors={computedBehaviors}
          currentPost={currentPost}
          isReadOnly={isReadOnly || !canPostRate}
          handleAction={handleAction}
          beginOperation={beginOperation}
          endOperation={endOperation}
        />
      )}
      {errorPostList && (
        <ErrorMessageContainer
          message={
            "Error Loading Posts to be rated please try reloading the page or contact support"
          }
        />
      )}
      {(postsLoading ||
        isFlushing ||
        isLoadingBlueprint ||
        boundaryPhase === "loading-next") && <LoadingSpinner />}
    </div>
  );
};

export default PostRate;
